/**
 * Normalizes an axios error into a user-facing message plus the metadata
 * needed to diagnose it later.
 *
 * The key distinction is network/gateway failure (no response object at all,
 * e.g. a tablet that dropped connectivity, or a proxy 502/504 with no JSON
 * body) vs. an actual server error. The backend's global exception handler
 * always returns `{ detail, error_id }` on a 500, so when `detail` is missing
 * we know the request never produced a real app response — surfacing that as a
 * connection problem (instead of a generic "Error doing X") tells the user what
 * actually happened. When the backend does return an `error_id`, we append it so
 * it can be matched against the `system_log` table.
 */
export interface ApiErrorInfo {
  /** Message to show the user. */
  message: string;
  /** HTTP status, when a response was received. */
  status?: number;
  /** Backend reference id (from the global exception handler), when present. */
  errorId?: string;
  /** True when no response was received (network/timeout/gateway failure). */
  isNetworkError: boolean;
}

export function describeApiError(error: any, fallback: string): ApiErrorInfo {
  const response = error?.response;

  // No response → the request never produced an app-level answer. This is a
  // connectivity/gateway failure, and nothing will have been logged server-side.
  if (!response) {
    return {
      message: "Couldn't reach the server. Check your connection, then refresh before retrying.",
      isNetworkError: true,
    };
  }

  const data = response.data;
  const isObj = data && typeof data === 'object';
  const detail: string | undefined = isObj ? data.detail : undefined;
  const errorId: string | undefined = isObj ? data.error_id : undefined;

  let message = detail || fallback;
  // The backend's 500 detail already embeds the reference id; only append it
  // when it isn't already in the message.
  if (errorId && !message.includes(errorId)) {
    message = `${message} (Ref: ${errorId})`;
  }

  return { message, status: response.status, errorId, isNetworkError: false };
}
